import { useEffect, useCallback, useRef, useState } from "react";
import { env } from "../../../shared/config/env";
import { useNavigate } from "react-router-dom";
import { useFormDataStore } from "../store/formDataStore";
import { WebSocketMessage } from "../types";
import { VideoData } from "../../../shared/interfaces/VideoData";
import { getAccessToken } from "@/shared/api/clients/tokenAccessor";
import { SaveCancelledError } from "./useReportSaving";

// 30s transport keepalive. Deliberately does NOT refresh the server lock TTL —
// the server ignores heartbeats for inactivity tracking (CJIS); only real user
// activity (edits + the interaction pings below) resets the editing timeout.
const HEARTBEAT_INTERVAL_MS = 30_000;
const INITIALIZATION_TIMEOUT_MS = 15_000;

// Any user interaction on the edit page resets the server-side editing
// timeout, not just edits — otherwise someone reading a report or presenting
// it (sales demos) gets the inactivity warning and is kicked out while
// visibly using the page. Pings are throttled: at most one per interval, sent
// on the $default route so the server's logAction handler records activity
// and pushes the lock TTL out.
//
// Only events the browser generates from real input hardware are listed.
// "scroll" is deliberately absent: programmatic scrolls (the transcript
// overlay auto-scrolling during video playback, chat autoscroll) emit
// trusted scroll events indistinguishable from a user's, which would renew
// the CJIS inactivity lock with nobody present. Every human way of scrolling
// already surfaces here as wheel / pointer / key / touch events.
const ACTIVITY_PING_INTERVAL_MS = 60_000;
const ACTIVITY_EVENTS = [
  "pointerdown",
  "pointermove",
  "keydown",
  "wheel",
  "touchstart",
] as const;

interface UseReportWebSocketProps {
  liveWebSocket: React.MutableRefObject<WebSocket | null>;
  reportID: string | undefined;
  enableEditing: boolean;
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
  showTimeoutWarning: boolean;
  setWarningState: (state: boolean) => void;
  setInactivityExpiryTimestamp: (timestamp: number) => void;
  refetchIncident: () => void;
  setIsSaving: (saving: boolean) => void;
  saveDeferredRef: React.MutableRefObject<{
    promise: Promise<void>;
    resolve: () => void;
    reject: (error: Error) => void;
    timeoutId: NodeJS.Timeout | null;
  } | null>;
  setLastModifiedTime: (date: Date | null) => void;
  setShowRMSCommit: (show: boolean) => void;
  setReportType: (type: string) => void;
  setVideoData: (data: VideoData | VideoData[] | null) => void;
  setWarnings: (warnings: string[]) => void;
  /**
   * True while a full-report run is regenerating this report. The backend
   * takes the report lock during the run, so we proactively close the editing
   * socket and suppress its disconnect-driven redirects — the officer stays on
   * the page watching the progress timer until the run completes.
   */
  isGeneratingFull?: boolean;
}

export const useReportWebSocket = ({
  liveWebSocket,
  reportID,
  enableEditing,
  showError,
  showSuccess,
  showTimeoutWarning,
  setWarningState,
  setInactivityExpiryTimestamp,
  refetchIncident,
  setIsSaving,
  saveDeferredRef,
  setLastModifiedTime,
  setShowRMSCommit,
  setReportType,
  setVideoData,
  setWarnings,
  isGeneratingFull = false,
}: UseReportWebSocketProps) => {
  const navigate = useNavigate();

  // Read inside the long-lived WS event handlers without re-subscribing them.
  // When true, a socket close is expected (full-report regeneration) and must
  // not trigger any redirect.
  const suppressRedirectRef = useRef(false);
  suppressRedirectRef.current = isGeneratingFull;

  // Ref to hold latest callback props — avoids stale closures in WS event handlers
  const handlersRef = useRef({
    showError, showSuccess, showTimeoutWarning, setWarningState,
    setInactivityExpiryTimestamp, refetchIncident, setIsSaving,
    setLastModifiedTime, setShowRMSCommit, setReportType,
    setVideoData, setWarnings,
  });
  handlersRef.current = {
    showError, showSuccess, showTimeoutWarning, setWarningState,
    setInactivityExpiryTimestamp, refetchIncident, setIsSaving,
    setLastModifiedTime, setShowRMSCommit, setReportType,
    setVideoData, setWarnings,
  };

  // Keep the transport private until the server grants the editing lock.
  // Save/regeneration consumers only receive a socket that can edit the report.
  const connectionRef = useRef<WebSocket | null>(null);
  const [readySession, setReadySession] = useState<{
    reportId: string;
    socket: WebSocket;
  } | null>(null);
  const isSessionReady =
    enableEditing && !isGeneratingFull &&
    readySession !== null && readySession.reportId === reportID;
  const sessionSocket = isSessionReady ? readySession.socket : null;

  // Latched synchronously when a sessionHeartbeatWarning arrives, cleared only
  // by an explicit Continue or by a server response to a real user action
  // (which renewed the lock server-side). The activity pings gate on this ref
  // rather than on the showTimeoutWarning prop alone: the prop only updates on
  // the next render, so a ping could otherwise slip through in the gap after
  // the warning arrives — silently renewing the session the modal is asking
  // the user to explicitly continue.
  const inactivityWarningActiveRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let terminalHandled = false;
    let sessionReady = false;
    let editingSocket: WebSocket | null = null;
    let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    let initializationTimer: ReturnType<typeof setTimeout> | null = null;

    setReadySession(null);
    inactivityWarningActiveRef.current = false;

    const stopTimers = () => {
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      if (initializationTimer) clearTimeout(initializationTimer);
      heartbeatTimer = null;
      initializationTimer = null;
    };

    const cancelPendingSave = () => {
      const pending = saveDeferredRef.current;
      if (!pending) return;
      if (pending.timeoutId) clearTimeout(pending.timeoutId);
      saveDeferredRef.current = null;
      pending.reject(new SaveCancelledError());
      handlersRef.current.setIsSaving(false);
    };

    const releaseConnection = () => {
      stopTimers();
      if (liveWebSocket.current === editingSocket) liveWebSocket.current = null;
      if (connectionRef.current === editingSocket) connectionRef.current = null;
      setReadySession(null);
      cancelPendingSave();
      if (editingSocket && editingSocket.readyState < WebSocket.CLOSING) {
        editingSocket.close();
      }
    };

    // Both a server denial envelope and the subsequent transport close arrive
    // here. Handle the terminal outcome once, including pending save cleanup.
    const finishSession = (code: number, reason?: string) => {
      if (cancelled || terminalHandled || suppressRedirectRef.current) return;
      terminalHandled = true;
      releaseConnection();

      if (code === 3008) {
        navigate("/", {
          replace: true,
          state: { message: "You were redirected due to inactivity." },
        });
        return;
      }

      let message: string;
      if (code === 3009) {
        message = "Another user is currently editing this report. Please try again later.";
      } else if (code === 3002) {
        message = "User is not authenticated. Please login again.";
      } else if (code === 3007) {
        message = "Lock was lost. Another user may have accessed this report.";
      } else {
        message = reason || (sessionReady
          ? "Connection to server lost unexpectedly. Please refresh the page to continue editing."
          : "Unable to connect to this report for editing. Please try opening it again.");
      }
      handlersRef.current.showError(message);
      navigate(code === 3002 ? "/login" : "/", { replace: true });
    };

    if (!isGeneratingFull && enableEditing && reportID) {
      // Include token retrieval and the opening handshake in the deadline.
      initializationTimer = setTimeout(() => {
        finishSession(1006);
      }, INITIALIZATION_TIMEOUT_MS);

      const connect = async () => {
        let tokenParam = "";
        try {
          const token = await getAccessToken();
          tokenParam = `&token=${encodeURIComponent(token)}`;
        } catch {
          // The authorizer will reject an unavailable token. A failed handshake
          // alone cannot tell us whether authentication or the network failed.
        }
        if (cancelled || terminalHandled) return;

        const wsURL = `${env.websocketsDomain}?reportID=${encodeURIComponent(reportID)}&sessionProtocol=2${tokenParam}`;
        try {
          editingSocket = new WebSocket(wsURL);
        } catch {
          finishSession(1006);
          return;
        }
        const socket = editingSocket;
        connectionRef.current = socket;
        let lastCloseCode: number | undefined;
        const isCurrent = () =>
          !cancelled && !terminalHandled &&
          !suppressRedirectRef.current && connectionRef.current === socket;

        socket.onopen = () => {
          if (!isCurrent()) return;
          // Lock acquisition happens after the upgrade so a rejection can be
          // delivered as a normal message, with its actual reason intact.
          try {
            socket.send(JSON.stringify({ action: "initialize" }));
          } catch {
            finishSession(1006);
          }
        };

        socket.onerror = (event) => {
          if (isCurrent()) console.error("[WebSocket] Connection error:", event);
        };

        socket.onclose = (event) => {
          if (!isCurrent()) return;
          finishSession(lastCloseCode ?? event.code);
        };

        socket.onmessage = (event) => {
          if (!isCurrent()) return;
          let data: WebSocketMessage;
          try {
            data = JSON.parse(event.data);
          } catch {
            console.error("[WebSocket] Invalid server message");
            return;
          }
          if (!data || typeof data !== "object") return;

          if (data.closeCode) lastCloseCode = data.closeCode;
          if (data.closing && data.closeCode) {
            finishSession(data.closeCode, data.error);
            return;
          }
          if (data.status === "duplicate") {
            finishSession(3009);
            return;
          }
          if (data.status === "ready") {
            if (sessionReady) return;
            sessionReady = true;
            if (initializationTimer) clearTimeout(initializationTimer);
            initializationTimer = null;
            liveWebSocket.current = socket;
            setReadySession({ reportId: reportID, socket });
            heartbeatTimer = setInterval(() => {
              if (isCurrent() && socket.readyState === WebSocket.OPEN) {
                socket.send(JSON.stringify({ action: "heartbeat" }));
              }
            }, HEARTBEAT_INTERVAL_MS);
            return;
          }
          if (!sessionReady) {
            if (data.error) finishSession(data.closeCode ?? 1011, data.error);
            return;
          }
          // Transport pings must not dismiss the inactivity warning.
          if (data.type === "ping") return;

          if (data.type === "sessionHeartbeatWarning") {
            inactivityWarningActiveRef.current = true;
            if (!handlersRef.current.showTimeoutWarning && data.expires) {
              handlersRef.current.setInactivityExpiryTimestamp(data.expires);
              handlersRef.current.setWarningState(true);
            }
            return;
          }

          // Any other server message is a response to a real user action (the
          // ping above never reaches here) — dismiss the warning modal.
          if (handlersRef.current.showTimeoutWarning) {
            handlersRef.current.setWarningState(false);
          }

          if (data.error) {
            handlersRef.current.showError(data.error);
            handlersRef.current.setIsSaving(false);
            if (saveDeferredRef.current) {
              if (saveDeferredRef.current.timeoutId) {
                clearTimeout(saveDeferredRef.current.timeoutId);
              }
              saveDeferredRef.current.reject(new Error(data.error));
              saveDeferredRef.current = null;
            }
            return;
          }

          // Only non-error responses prove the lock was renewed server-side:
          // every handler renews before pushing state, but error pushes can
          // come from a failed validateLock (no renewal) — so the activity-ping
          // latch lifts here, after the error early-return above.
          inactivityWarningActiveRef.current = false;

          if (data.success) {
            handlersRef.current.setIsSaving(false);
            handlersRef.current.showSuccess(data.success);

            if (saveDeferredRef.current) {
              if (saveDeferredRef.current.timeoutId) {
                clearTimeout(saveDeferredRef.current.timeoutId);
              }
              saveDeferredRef.current.resolve();
              saveDeferredRef.current = null;
            }

            if (data.narrativeUpdated) {
              handlersRef.current.refetchIncident();
            }
          }

          if (data.exit) {
            terminalHandled = true;
            releaseConnection();
            const { destroyStore } = useFormDataStore.getState();
            destroyStore();

            if (data.timeout) {
              navigate(`/`, {
                replace: true,
                state: { message: "You were redirected due to inactivity." },
              });
            } else {
              navigate("/");
            }
            return;
          }

          if (data.newObject) {
            const { setFormData } = useFormDataStore.getState();
            setFormData(data.newObject);
            handlersRef.current.setLastModifiedTime(
              data.lastModified ? new Date(data.lastModified) : new Date()
            );
          }

          if (typeof data.supportsRMS !== "undefined") {
            handlersRef.current.setShowRMSCommit(data.supportsRMS);
          }

          if (data.reportType) {
            handlersRef.current.setReportType(data.reportType);
          }

          if (data.video) {
            handlersRef.current.setVideoData(data.video);
          }

          if (data.warnings && data.warnings.length > 0) {
            handlersRef.current.setWarnings(data.warnings);
          }
        };
      };
      void connect();
    }

    return () => {
      // A late close/message from this transport must never affect its
      // replacement after a report change or full-report regeneration.
      cancelled = true;
      releaseConnection();
    };
  }, [reportID, enableEditing, isGeneratingFull, navigate, liveWebSocket, saveDeferredRef]);

  // Reset the server editing timeout on ANY user interaction (mouse, keys,
  // wheel, touch), throttled to one ping per ACTIVITY_PING_INTERVAL_MS.
  // Deliberately paused while the inactivity warning is active: by then the
  // user was genuinely idle, and continuing the session must be the modal's
  // explicit "Continue" click, not an incidental mouse twitch.
  const lastActivityPingRef = useRef(0);
  useEffect(() => {
    if (!enableEditing) return;

    const onUserActivity = (event: Event) => {
      // Only browser-generated input counts. Script-dispatched synthetic
      // events (extensions, "keep awake" tools, libraries) must not be able
      // to hold the CJIS inactivity lock open on an unattended session.
      if (!event.isTrusted) return;
      if (
        inactivityWarningActiveRef.current ||
        handlersRef.current.showTimeoutWarning
      ) {
        return;
      }
      const socket = liveWebSocket.current;
      if (!socket || socket.readyState !== WebSocket.OPEN) return;

      const now = Date.now();
      if (now - lastActivityPingRef.current < ACTIVITY_PING_INTERVAL_MS) return;
      lastActivityPingRef.current = now;

      socket.send(JSON.stringify({ action: "User activity" }));
    };

    // Capture phase so component handlers that stopPropagation still count
    // as activity.
    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, onUserActivity, {
        capture: true,
        passive: true,
      });
    }
    return () => {
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, onUserActivity, {
          capture: true,
        });
      }
    };
  }, [enableEditing, liveWebSocket]);

  const handleUserAction = useCallback((action: string) => {
    if (showTimeoutWarning || !enableEditing) return;

    // Activity logging is best effort. Connection lifecycle handlers own the
    // error message; startup and repeated keystrokes must not produce toasts.
    if (!liveWebSocket.current ||
        liveWebSocket.current.readyState !== WebSocket.OPEN) return;
    liveWebSocket.current.send(JSON.stringify({ action }));
  }, [showTimeoutWarning, enableEditing, liveWebSocket]);

  const continueSession = useCallback(() => {
     inactivityWarningActiveRef.current = false;
     setWarningState(false);
     if (
      liveWebSocket.current &&
      liveWebSocket.current.readyState === WebSocket.OPEN
    ) {
      liveWebSocket.current.send(
        JSON.stringify({ action: "Continue session" })
      );
    }
  }, [setWarningState, liveWebSocket]);

  return { handleUserAction, continueSession, isSessionReady, sessionSocket };
};
